import { useMemo, useState } from 'react'

import { Card, Progress } from '@aptive-env/storybook'
import { AptiveIcon } from '@aptive-env/storybook'
import { Home, Mile, Vehicle, Service } from 'icons'
import { IStatistics } from 'interface/map'
import { minutesToHHMM } from 'utils/minutesToHHMM'
import { routingColors } from 'constants/mockup'
import { DefaultAvatar } from 'icons'

import styles from './index.module.scss'

interface ServiceCardProps {
  avatar?: string
  name: string
  statistics: IStatistics | null
  completionPercentage: number
  isStarted: boolean
  isAtRisk: boolean
  index: number
}

const ServiceCard = ({ avatar, name, statistics, completionPercentage, isStarted, isAtRisk, index }: ServiceCardProps) => {
  const [expand, setExpand] = useState(false)

  const generateAvatar = (avatar: any, index: number) => {
    const avatarBorderStyle = {
      border: `2px solid ${routingColors[index % routingColors.length]}`,
    }
    return (
      avatar
        ? <img className={`${styles.avatar}`} src={`data:image/png;base64,${avatar}`} alt="" style={avatarBorderStyle} />
        : 
        <div className={`${styles.avatar} flex items-center justify-center`} style={avatarBorderStyle}>
          <DefaultAvatar />
        </div>
    )
  }

  const totalAppointments = useMemo(() => {
    return statistics ? statistics.total_appointments : 0
  }, [statistics])

  const totalDriveMiles = useMemo(() => {
    return statistics ? statistics.total_drive_miles : 0
  }, [statistics])

  const totalDriveTime = useMemo(() => {
    return minutesToHHMM(statistics ? statistics.total_drive_time_minutes : 0)
  }, [statistics])

  const totalServiceTime = useMemo(() => {
    return minutesToHHMM(statistics ? statistics.total_service_time_minutes : 0)
  }, [statistics])

  return (
    <Card className={`flex flex-col w-[343px] bg-ui-snow rounded shadow-md bg-[#ededed] overflow-hidden border ${isAtRisk ? 'border-[#F05252]' : 'border-[#e5e7eb]'}`}>
      <div className="flex items-center gap-2 p-4">
        {generateAvatar(avatar, index)}
        <span className="font-inter text-base font-normal text-[#4B5563] leading-[22px] flex-grow">{name}</span>
        <div className="cursor-pointer">
          <AptiveIcon className="w-[24px] h-[24px] stroke-ui-gray-500 fill-none" icon="dotsVertical" />
        </div>
        <div className="cursor-pointer" aria-label="toggle-btn" onClick={() => setExpand(!expand)}>
          {expand ? 
            <AptiveIcon className="w-[20px] h-[20px] stroke-ui-gray-500 fill-none" icon="chevronUp" />
            :
            <AptiveIcon className="w-[20px] h-[20px] stroke-ui-gray-500 fill-none" icon="chevronDown" />
          }
        </div>
      </div>
      <div className={`${expand ? 'block' : 'hidden'}`} aria-label="detail-panel">
        <div className="p-4 pt-0">
          <div className="flex gap-2 h-[40px] mt-2">
            <div className="flex-grow" />
            <div className="flex w-[59px] service-item items-center justify-center bg-white rounded"><Home className={styles.svg} /></div>
            <div className="flex w-[59px] service-item items-center justify-center bg-white rounded"><Mile className={styles.svg} /></div>
            <div className="flex w-[59px] service-item items-center justify-center bg-white rounded"><Vehicle className={styles.svg} /></div>
            <div className="flex w-[59px] service-item items-center justify-center bg-white rounded"><Service className={styles.svg} /></div>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex-grow text-right">
              <span className="font-inter font-bold text-xs leading-5">Plan</span>
            </div>
            <div className="flex rounded bg-[#F9FAFB] w-[59px] py-2 items-center justify-center">
              <span className="font-inter font-bold text-[15px] leading-5">{totalAppointments}</span>
            </div>
            <div className="flex rounded bg-[#F9FAFB] w-[59px] py-2 items-center justify-center">
              <span className="font-inter font-bold text-[15px] leading-5">{totalDriveMiles}</span>
            </div>
            <div className="flex rounded bg-[#F9FAFB] w-[59px] py-2 items-center justify-center">
              <span className="font-inter font-bold text-[15px] leading-5">{totalDriveTime}</span>
            </div>
            <div className="flex rounded bg-[#F9FAFB] w-[59px] py-2 items-center justify-center">
              <span className="font-inter font-bold text-[15px] leading-5">{totalServiceTime}</span>
            </div>
          </div>
        </div>
      </div>
      {isStarted && (
        <div className={`h-[62px] px-3 border border-l-0 border-r-0 border-b-0 ${isAtRisk ? 'bg-[#F05252] border-[#F05252]' : 'bg-[#f9fafb] border-[#E5E7EB]'}`}>
          <div className={`text-[14px] font-normal mt-3 ${isAtRisk ? 'text-white' : 'text-[#4B5563]'}`}>
            Route Completion
          </div>
          {isAtRisk ? (
            <Progress
              className="mt-[4px] [&>.progresslabel]:text-white [&>.progresslabel]:top-[-20px] [&>.progressvalue]:h-[10px]"
              value={completionPercentage}
            />
          ) : (
            <Progress
              className={`mt-[4px] [&>.progresslabel]:top-[-20px] [&>.progressvalue]:h-[10px] ${completionPercentage > 50 ? '[&>.progressvalue]:bg-[#84E1BC]' : ''}`}
              value={completionPercentage}
            />
          )}
        </div>
      )}
    </Card>
  )
}

export default ServiceCard
